import React, { useEffect, useState } from "react";
import "../../pages/dashboard/DashboardPage.css";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import BranchList from "../../components/BranchList/BranchList";
import DepartmentList from "../../components/DepartmentList/DepartmentList";
import StaffAppointmentCard from "../../components/StaffAppointmentCard/StaffAppointmentCard";
import {
  fetchAllDepartments,
  setSelectedDepartmentId,
  clearSelectedDepartment,
} from "../../store/slices/departmentSlice";
import {
  acceptNext,
  fetchDepartmentQueue,
  clearCurrentAppointment,
  clearQueue,
  clearInProgress,
  completeAppointment,
} from "../../store/slices/appointmentSlice";

export default function Dashboard() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const {
    allDepartments,
    allDepartmentsLoading,
    allDepartmentsError,
    selectedDepartmentId,
  } = useAppSelector((state) => state.department);
  const {
    currentAppointment,
    queue,
    queueLoading,
    inProgressList,
    inProgressLoading,
    inProgressError,
    acceptNextLoading,
    acceptNextError,
    completeLoading,
    completeError,
  } = useAppSelector((state) => state.appointment);

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeNotes, setCompleteNotes] = useState("");
  const [selectedAppointmentForComplete, setSelectedAppointmentForComplete] =
    useState(null);

  // institutionStaff: tüm departmanları yükle
  useEffect(() => {
    if (user.userType === "institutionStaff") {
      dispatch(fetchAllDepartments());
    }
  }, [user.userType, dispatch]);

  // institutionStaffId: user.institutionStaffId backend'den gelir, yoksa user.id kullanılır
  const institutionStaffId = user?.institutionStaffId ?? user?.id;

  // Seçili departman değişince kuyruk ve kabul state'ini temizle, pending + in_progress çek
  useEffect(() => {
    if (!selectedDepartmentId) {
      dispatch(clearCurrentAppointment());
      dispatch(clearQueue());
      dispatch(clearInProgress());
      return;
    }
    dispatch(clearCurrentAppointment());
    // Bekleyen sıra (pending)
    dispatch(fetchDepartmentQueue(selectedDepartmentId));
    // Şube çalışanının bitmemiş işlemleri (in_progress)
    if (institutionStaffId) {
      dispatch(
        fetchDepartmentQueue({
          departmentId: selectedDepartmentId,
          institutionStaffId,
          status: "in_progress",
        }),
      );
    }
  }, [selectedDepartmentId, institutionStaffId, dispatch]);

  const selectedDepartment = allDepartments.find(
    (d) => d.id === selectedDepartmentId,
  );

  const handleAcceptNext = () => {
    if (!selectedDepartmentId) return;
    dispatch(acceptNext(selectedDepartmentId)).then(() => {
      dispatch(fetchDepartmentQueue(selectedDepartmentId));
      if (institutionStaffId) {
        dispatch(
          fetchDepartmentQueue({
            departmentId: selectedDepartmentId,
            institutionStaffId,
            status: "in_progress",
          }),
        );
      }
    });
  };

  const handleOpenCompleteModal = (appointment) => {
    setSelectedAppointmentForComplete(appointment);
    setShowCompleteModal(true);
  };
  const handleCloseCompleteModal = () => {
    setShowCompleteModal(false);
    setCompleteNotes("");
    setSelectedAppointmentForComplete(null);
  };

  const handleCompleteAppointment = () => {
    if (!selectedAppointmentForComplete?.id) return;
    const notes =
      completeNotes.trim().length > 0 ? completeNotes.trim() : undefined;
    if (notes != null && notes.length > 1000) return; // validasyon
    dispatch(
      completeAppointment({
        appointmentId: selectedAppointmentForComplete.id,
        notes,
      }),
    ).then((result) => {
      if (completeAppointment.fulfilled.match(result)) {
        handleCloseCompleteModal();
        if (selectedDepartmentId) {
          dispatch(fetchDepartmentQueue(selectedDepartmentId));
          if (institutionStaffId) {
            dispatch(
              fetchDepartmentQueue({
                departmentId: selectedDepartmentId,
                institutionStaffId,
                status: "in_progress",
              }),
            );
          }
        }
      }
    });
  };

  // institutionStaff: departman kartları + seçili departman ekranı
  if (user.userType === "institutionStaff") {
    if (allDepartmentsLoading) {
      return (
        <div className="dashboard-page">
          <h2>Personel Paneli</h2>
          <p>Departmanlar yükleniyor...</p>
        </div>
      );
    }

    if (allDepartmentsError) {
      return (
        <div className="dashboard-page">
          <h2>Personel Paneli</h2>
          <p className="staff-page-error">Hata: {allDepartmentsError}</p>
        </div>
      );
    }

    // Seçili departman ekranı: üstte Kabul Et, sağda sıra bekleyenler
    if (selectedDepartmentId && selectedDepartment) {
      return (
        <div className="dashboard-page">
          <div className="staff-department-header">
            <button
              type="button"
              className="staff-back-btn"
              onClick={() => dispatch(clearSelectedDepartment())}
            >
              ← Departmanlar
            </button>
            <h2 className="staff-department-title">
              {selectedDepartment.name}
            </h2>
            <p className="staff-department-branch">
              {selectedDepartment.branchName}
            </p>
          </div>

          <div className="staff-department-content">
            <div className="staff-main-area">
              <div className="staff-accept-section">
                <button
                  type="button"
                  className="staff-accept-btn"
                  onClick={handleAcceptNext}
                  disabled={acceptNextLoading}
                >
                  {acceptNextLoading ? "İşleniyor..." : "Kabul Et"}
                </button>
                {acceptNextError && (
                  <p className="staff-error">Hata: {acceptNextError}</p>
                )}
              </div>

              {(currentAppointment || inProgressList?.length > 0) && (
                <div className="staff-current-appointment">
                  <h3>Kabul edilen randevu</h3>
                  {inProgressLoading ? (
                    <p>Sıra yükleniyor...</p>
                  ) : (
                    <>
                      <div className="staff-in-progress-cards">
                        {inProgressList?.map((item) => (
                          <StaffAppointmentCard
                            key={item.id}
                            appointment={item}
                            onComplete={handleOpenCompleteModal}
                            completeLoading={completeLoading}
                          />
                        ))}
                      </div>
                      {inProgressError && (
                        <p className="staff-error">{inProgressError}</p>
                      )}
                    </>
                  )}

                  {showCompleteModal && (
                    <div
                      className="staff-complete-modal-overlay"
                      onClick={handleCloseCompleteModal}
                    >
                      <div
                        className="staff-complete-modal"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h4>Sırayı tamamla</h4>
                        <p className="staff-complete-modal-hint">
                          İsteğe bağlı: işlem notu ekleyebilirsiniz (maks. 1000
                          karakter).
                        </p>
                        <textarea
                          className="staff-complete-notes"
                          placeholder="İşlem notu (isteğe bağlı)"
                          value={completeNotes}
                          onChange={(e) =>
                            setCompleteNotes(e.target.value.slice(0, 1000))
                          }
                          maxLength={1000}
                          rows={4}
                        />
                        <span className="staff-complete-notes-count">
                          {completeNotes.length}/1000
                        </span>
                        {completeError && (
                          <p className="staff-error">{completeError}</p>
                        )}
                        <div className="staff-complete-modal-actions">
                          <button
                            type="button"
                            className="staff-complete-modal-cancel"
                            onClick={handleCloseCompleteModal}
                          >
                            İptal
                          </button>
                          <button
                            type="button"
                            className="staff-complete-modal-submit"
                            onClick={handleCompleteAppointment}
                            disabled={completeLoading}
                          >
                            {completeLoading ? "Tamamlanıyor..." : "Tamamla"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="staff-queue-panel">
              <h3>Sıra bekleyenler</h3>
              {queueLoading ? (
                <p>Sıra yükleniyor...</p>
              ) : Array.isArray(queue) && queue.length > 0 ? (
                <ul className="staff-queue-list">
                  {queue.map((item) => (
                    <li key={item.id} className="staff-queue-item">
                      <span className="staff-queue-number">
                        #{item.queueNumber}
                      </span>
                      <span>{item.user?.name ?? "—"}</span>
                      <span className="staff-queue-phone">
                        {item.user?.phone ?? ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="staff-queue-empty">
                  Bu departmanda sıra bekleyen yok.
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Departman kartları listesi
    return (
      <div className="dashboard-page">
        <h2>Personel Paneli</h2>
        <p className="staff-subtitle">Bir departman seçin</p>

        {!allDepartments?.length ? (
          <p className="staff-empty">Henüz departman bulunmuyor.</p>
        ) : (
          <div className="staff-department-grid">
            {allDepartments.map((dept) => (
              <button
                type="button"
                key={dept.id}
                className="staff-department-card"
                onClick={() => dispatch(setSelectedDepartmentId(dept.id))}
              >
                <span className="staff-department-card-name">{dept.name}</span>
                <span className="staff-department-card-branch">
                  {dept.branchName}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // institution değilse normal içerik
  if (user.userType !== "institution") {
    return (
      <div className="dashboard-page">
        <h2>Dashboard</h2>
        <p>Bu sayfa içeriği henüz eklenmedi.</p>
      </div>
    );
  }

  // Institution kullanıcıları için şube ekleme formu
  return (
    <div className="dashboard-page">
      <h2>Şube Yönetimi</h2>
      <div className="dashboard-page-content">
        <BranchList />
        <DepartmentList />
      </div>
    </div>
  );
}
